import { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import StatCard from '@/components/base/StatCard';
import Badge from '@/components/base/Badge';
import DonutStat from '@/components/base/charts/DonutStat';
import TrendBarChart from '@/components/base/charts/TrendBarChart';
import GaugeStat from '@/components/base/charts/GaugeStat';
import ExchangeRateCards from '@/components/feature/ExchangeRateCards';
import * as DB from '@/lib/supabaseDb';
import * as LoanDB from '@/lib/personalLoansDb';
import { useDataRefresh } from '@/hooks/useDataRefresh';
import { formatTRY } from '@/lib/currencyFormat';
import { paymentStatus, deliveryStatus } from '@/lib/statusDefs';

const DONUT_COLORS = [
  'oklch(var(--primary-500))',
  'oklch(var(--accent-500))',
  'oklch(var(--secondary-400))',
  'oklch(var(--primary-300))',
  'oklch(var(--accent-300))',
];

function TrackingRow({ label, value, icon, colorClass, navigateTo }: { label: string; value: string; icon: string; colorClass: string; navigateTo: string }) {
  const nav = useNavigate();
  return (
    <div onClick={() => nav(navigateTo)} className="flex items-center justify-between py-2.5 px-3 rounded-lg hover:bg-background-50 transition-colors cursor-pointer group" role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter') nav(navigateTo); }}>
      <div className="flex items-center gap-3 min-w-0">
        <span className={`w-8 h-8 rounded-lg ${colorClass} flex items-center justify-center flex-shrink-0`}>
          <i className={`${icon} text-sm text-background-50`}></i>
        </span>
        <span className="text-sm font-medium text-foreground-700 truncate">{label}</span>
      </div>
      <span className="text-sm font-bold text-foreground-900 whitespace-nowrap ml-3">{value}</span>
    </div>
  );
}

function TrackerChip({ label, count, icon, colorClass, navigateTo, hidden }: { label: string; count: number; icon: string; colorClass: string; navigateTo: string; hidden: boolean }) {
  const nav = useNavigate();
  if (hidden && count === 0) return null;
  return (
    <div onClick={() => nav(navigateTo)} className="flex items-center justify-between p-3 rounded-xl bg-background-50 border border-background-200/60 hover:border-background-300/60 transition-colors cursor-pointer group" role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter') nav(navigateTo); }}>
      <div className="flex items-center gap-2.5 min-w-0">
        <span className={`w-8 h-8 rounded-lg ${colorClass} flex items-center justify-center flex-shrink-0`}>
          <i className={`${icon} text-sm text-background-50`}></i>
        </span>
        <span className="text-xs font-medium text-foreground-600 truncate">{label}</span>
      </div>
      <span className="text-lg font-bold text-foreground-900 whitespace-nowrap ml-2">{count}</span>
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const today = new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/Istanbul' });
  const { version } = useDataRefresh();

  const [summary, setSummary] = useState<DB.DashboardSummary | null>(null);
  const [allSales, setAllSales] = useState<any[]>([]);
  const [promissoryNotes, setPromissoryNotes] = useState<any[]>([]);
  const [deliveries, setDeliveries] = useState<any[]>([]);
  const [saleItemsAll, setSaleItemsAll] = useState<any[]>([]);
  const [promiseCounts, setPromiseCounts] = useState<DB.PromiseFollowUpCounts | null>(null);
  const [loanSummary, setLoanSummary] = useState<LoanDB.PersonalLoanSummary | null>(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [summaryRes, salesRes, notesRes, deliveriesRes, itemsRes, promiseRes, loanSumRes] = await Promise.all([
        DB.getDashboardSummary(),
        DB.getSales(),
        DB.getPromissoryNotes(),
        DB.getDeliveries(),
        DB.getAllSaleItems(),
        DB.getPromiseFollowUpCounts(),
        LoanDB.getPersonalLoanSummary(),
      ]);
      if (summaryRes.error) {
        if (summaryRes.error !== 'İşletme bilgisi bulunamadı') {
          setError(summaryRes.error);
        }
      }
      setSummary(summaryRes.data);
      setAllSales((salesRes.data as any[]) || []);
      setPromissoryNotes((notesRes.data as any[]) || []);
      setDeliveries((deliveriesRes.data as any[]) || []);
      setSaleItemsAll((itemsRes.data as any[]) || []);
      setPromiseCounts(promiseRes.data);
      setLoanSummary(loanSumRes.data);
    } catch {
      setError('Veri yüklenirken hata oluştu.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData, version]);

  const salesData = useMemo(() => allSales.slice(0, 7), [allSales]);
  const s = summary;

  const categoryDistribution = useMemo(() => {
    const cats: Record<string, number> = {};
    salesData.forEach((sale: any) => { const cat = sale.product_category || 'Diğer'; cats[cat] = (cats[cat] || 0) + (sale.total_amount || 0); });
    return Object.entries(cats).map(([name, value], index) => ({ name, value, color: DONUT_COLORS[index % DONUT_COLORS.length] }));
  }, [salesData]);

  const salesDataTotal = useMemo(() => salesData.reduce((sum: number, sale: any) => sum + (sale.total_amount || 0), 0), [salesData]);

  const weeklyTrend = useMemo(() => {
    const totalsByDate: Record<string, number> = {};
    allSales.forEach((sale: any) => {
      if (!sale.sale_date) return;
      totalsByDate[sale.sale_date] = (totalsByDate[sale.sale_date] || 0) + (sale.total_amount || 0);
    });
    const days: { label: string; value: number }[] = [];
    for (let i = 6; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      const dateStr = d.toLocaleDateString('en-CA', { timeZone: 'Europe/Istanbul' });
      const label = d.toLocaleDateString('tr-TR', { weekday: 'short', timeZone: 'Europe/Istanbul' });
      days.push({ label, value: totalsByDate[dateStr] || 0 });
    }
    return days;
  }, [allSales]);

  const collectionsRatioPercent = useMemo(() => {
    if (!s || !s.today_revenue) return null;
    return Math.round((s.today_collections / s.today_revenue) * 100);
  }, [s]);

  const upcomingDeliveries = useMemo(() => {
    const weekLater = new Date();
    weekLater.setDate(weekLater.getDate() + 7);
    const weekLaterStr = weekLater.toLocaleDateString('en-CA', { timeZone: 'Europe/Istanbul' });
    return deliveries
      .filter((d: any) => (d.status === 'Planlandı' || d.status === 'Hazırlanıyor') && d.delivery_date >= today && d.delivery_date <= weekLaterStr)
      .slice(0, 5);
  }, [deliveries, today]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh]">
        <div className="w-12 h-12 rounded-full border-2 border-primary-200 border-t-primary-500 animate-spin mb-4"></div>
        <h2 className="text-lg font-semibold text-foreground-700">Dashboard yükleniyor...</h2>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh]">
        <div className="w-16 h-16 rounded-2xl bg-red-50 flex items-center justify-center mb-4"><i className="ri-error-warning-line text-2xl text-red-400"></i></div>
        <h2 className="text-lg font-semibold text-foreground-700">Yükleme başarısız</h2>
        <p className="text-sm text-foreground-400 mt-1">{error}</p>
        <button onClick={loadData} className="btn-primary mt-4 cursor-pointer flex items-center gap-2"><i className="ri-refresh-line"></i>Tekrar Dene</button>
      </div>
    );
  }

  return (
    <div className="space-y-6 md:space-y-8">
      {/* Başlık */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading">Dashboard</h2>
          <p className="text-sm text-foreground-400 mt-0.5">İşletme genel durum özeti</p>
        </div>
      </div>

      {/* 1. ANA FİNANS ÖZETİ — 4 sade kart (referans "analytic" stili, ikonsuz) */}
      <div>
        <h4 className="text-[11px] font-semibold text-foreground-300 uppercase tracking-widest mb-3"><a href="#finance" className="hover:text-foreground-500 transition-colors">Finansal Özet</a></h4>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
          <StatCard size="lg" title="Bugünkü Ciro" value={s ? formatTRY(s.today_revenue) : '...'} navigateTo="/ciro-raporlar?period=today&type=revenue&src=dashboard" detailLabel="Detay" />
          <StatCard size="lg" title="Bugünkü Tahsilat" value={s ? formatTRY(s.today_collections) : '...'} navigateTo="/odemeler?period=today&status=completed&src=dashboard" detailLabel="Detay" />
          <StatCard size="lg" title="Toplam Alacak" value={s ? formatTRY(s.total_receivables) : '...'} navigateTo="/odemeler?filter=all_outstanding&src=dashboard" detailLabel="Detay" />
          <StatCard size="lg" title="Bu Ay Net Kâr" value={s ? formatTRY(s.month_net_profit) : '...'} navigateTo="/ciro-raporlar?period=this_month&type=net_profit&src=dashboard" detailLabel="Detay" />
        </div>
      </div>

      {/* 2. OPERASYON ÖZETİ — 5 küçük kart */}
      <div>
        <h4 className="text-[11px] font-semibold text-foreground-300 uppercase tracking-widest mb-3"><a href="#operations" className="hover:text-foreground-500 transition-colors">Operasyon Özeti</a></h4>
        <div className="grid grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
          <StatCard size="sm" title="Toplam Müşteri" value={s ? s.total_customers.toLocaleString('tr-TR') : '...'} icon="ri-team-line" colorClass="bg-accent-500" navigateTo="/musteriler?filter=all&src=dashboard" detailLabel="Listele" />
          <StatCard size="sm" title="Stoktaki Ürün" value={s ? s.in_stock_products.toLocaleString('tr-TR') : '...'} icon="ri-archive-line" colorClass="bg-secondary-500" navigateTo="/stok-urunler?filter=in_stock&src=dashboard" detailLabel="Listele" />
          <StatCard size="sm" title="Bugünkü Teslimat" value={s ? String(s.today_deliveries) : '...'} icon="ri-truck-line" colorClass="bg-primary-400" navigateTo="/teslimatlar?date=today&src=dashboard" detailLabel="Listele" />
          <StatCard size="sm" title="Aktif İcra Dosyası" value={s ? s.active_enforcement_count.toLocaleString('tr-TR') : '...'} icon="ri-scales-3-line" colorClass="bg-red-500" navigateTo="/icra-takibi?filter=active&src=dashboard" detailLabel="Listele" />
          <StatCard size="sm" title="Aktif Personel" value={s ? String(s.active_employees) : '...'} icon="ri-user-star-line" colorClass="bg-emerald-400" navigateTo="/personel?status=active&src=dashboard" detailLabel="Listele" />
        </div>
      </div>

      {/* 3. ANALİTİK ÜÇLÜSÜ — Satış Dağılımı (donut) / Haftalık Ciro Trendi (bar+trend) / Bugünkü Tahsilat Oranı (gauge) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-5">
        <div className="stat-card">
          <h3 className="text-sm font-semibold text-foreground-800 mb-4">Satış Dağılımı</h3>
          {categoryDistribution.length > 0 ? (
            <DonutStat data={categoryDistribution} totalLabel="Toplam Tutar" totalValue={formatTRY(salesDataTotal)} valueFormatter={(v) => formatTRY(v)} height={220} />
          ) : (
            <div className="flex flex-col items-center justify-center py-10 gap-2"><div className="w-12 h-12 rounded-full bg-background-100 flex items-center justify-center"><i className="ri-pie-chart-line text-xl text-foreground-300"></i></div><p className="text-sm text-foreground-400">Henüz veri yok</p></div>
          )}
        </div>

        <div className="stat-card">
          <h3 className="text-sm font-semibold text-foreground-800 mb-4">Haftalık Ciro Trendi</h3>
          <TrendBarChart data={weeklyTrend} height={220} valueFormatter={(v) => formatTRY(v)} />
        </div>

        <div className="stat-card flex flex-col">
          <h3 className="text-sm font-semibold text-foreground-800 mb-2">Bugünkü Tahsilat Oranı</h3>
          {collectionsRatioPercent !== null ? (
            <GaugeStat label="Tahsilat / Ciro" percent={collectionsRatioPercent} displayValue={`%${collectionsRatioPercent}`} height={160} />
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center py-6 gap-2"><i className="ri-percent-line text-2xl text-foreground-300"></i><p className="text-sm text-foreground-400 text-center">Bugün henüz ciro yok</p></div>
          )}
        </div>
      </div>

      {/* 4. FİNANSAL TAKİP + BUGÜNKÜ TAKİPLER + İCRA ÖZETİ */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-5">
        {/* Finansal Takip Paneli */}
        <div className="stat-card p-4 md:p-5">
          <h3 className="text-sm font-semibold text-foreground-800 mb-4">Finansal Takip</h3>
          <div className="space-y-1">
            <TrackingRow label="Bekleyen Senet" value={s ? formatTRY(s.pending_notes_total) : '...'} icon="ri-file-list-3-line" colorClass="bg-amber-400" navigateTo="/senetler?filter=pending&src=dashboard" />
            <TrackingRow label="Geciken Senet" value={s ? formatTRY(s.overdue_notes_total) : '...'} icon="ri-alert-line" colorClass="bg-red-400" navigateTo="/senetler?filter=overdue&src=dashboard" />
            <TrackingRow label="İcradaki Alacak" value={s ? formatTRY(s.enforcement_total_debt) : '...'} icon="ri-government-line" colorClass="bg-rose-400" navigateTo="/icra-takibi?filter=active_outstanding&src=dashboard" />
            {s && s.pending_salary > 0 && (
              <TrackingRow label="Bekleyen Maaş" value={formatTRY(s.pending_salary)} icon="ri-bank-card-line" colorClass="bg-amber-500" navigateTo="/personel/odemeler?filter=pending_salary&src=dashboard" />
            )}
            {s && s.pending_salary === 0 && (
              <div className="flex items-center justify-between py-2.5 px-3 rounded-lg opacity-40">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-bank-card-line text-sm text-amber-400"></i>
                  </span>
                  <span className="text-sm text-foreground-400">Bekleyen Maaş</span>
                </div>
                <span className="text-sm text-foreground-300 whitespace-nowrap ml-3">{formatTRY(0)}</span>
              </div>
            )}
            {loanSummary && loanSummary.overdue_amount > 0 && (
              <TrackingRow label="Elden Borç (Gecikmiş)" value={formatTRY(loanSummary.overdue_amount)} icon="ri-hand-coin-line" colorClass="bg-orange-400" navigateTo="/personal-loans?filter=overdue&src=dashboard" />
            )}
            {loanSummary && loanSummary.total_remaining > 0 && (
              <TrackingRow label="Elden Borç (Toplam)" value={formatTRY(loanSummary.total_remaining)} icon="ri-hand-coin-line" colorClass="bg-amber-400" navigateTo="/personal-loans?src=dashboard" />
            )}

          </div>
        </div>

        {/* Bugünkü Takipler Paneli */}
        <div className="stat-card p-4 md:p-5">
          <h3 className="text-sm font-semibold text-foreground-800 mb-4">Bugünkü Takipler</h3>
          <div className="grid grid-cols-2 gap-2.5">
            <TrackerChip label="Bugün Aranacaklar" count={promiseCounts?.today_call ?? 0} icon="ri-phone-line" colorClass="bg-sky-400" navigateTo="/odeme-sozleri?filter=today_call&src=dashboard" hidden={false} />
            <TrackerChip label="Tekrar Aranacaklar" count={promiseCounts?.recall ?? 0} icon="ri-phone-find-line" colorClass="bg-orange-400" navigateTo="/odeme-sozleri?filter=recall&src=dashboard" hidden={true} />
            <TrackerChip label="Akşam Ödeyecekler" count={promiseCounts?.evening ?? 0} icon="ri-moon-line" colorClass="bg-purple-400" navigateTo="/odeme-sozleri?filter=evening&src=dashboard" hidden={true} />
            <TrackerChip label="Sözünü Tutmayanlar" count={promiseCounts?.defaulted ?? 0} icon="ri-user-unfollow-line" colorClass="bg-red-400" navigateTo="/odeme-sozleri?filter=defaulted&src=dashboard" hidden={true} />
          </div>
          {(!promiseCounts || (promiseCounts.today_call === 0 && promiseCounts.recall === 0 && promiseCounts.evening === 0 && promiseCounts.defaulted === 0)) && (
            <p className="text-xs text-foreground-400 text-center pt-3 pb-1">Henüz takip kaydı yok</p>
          )}
        </div>

        {/* İcra Takibi — koyu kontrast widget (gerçek veri, uydurma progress yok) */}
        <div className="rounded-2xl bg-foreground-900 text-background-50 p-4 md:p-5 flex flex-col justify-between">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <span className="w-8 h-8 rounded-lg bg-background-50/10 flex items-center justify-center flex-shrink-0">
                <i className="ri-scales-3-line text-base"></i>
              </span>
              <h3 className="text-sm font-semibold">İcra Takibi</h3>
            </div>
            <p className="text-2xl font-bold font-heading">{s ? s.active_enforcement_count.toLocaleString('tr-TR') : '...'}</p>
            <p className="text-xs text-background-50/60 mb-3">Aktif dosya</p>
            <p className="text-lg font-bold font-heading">{s ? formatTRY(s.enforcement_total_debt) : '...'}</p>
            <p className="text-xs text-background-50/60">İcradaki toplam alacak</p>
          </div>
          <button
            onClick={() => navigate('/icra-takibi?filter=active&src=dashboard')}
            className="mt-4 w-full flex items-center justify-center gap-2 bg-background-50/10 hover:bg-background-50/20 transition-colors rounded-lg py-2.5 text-sm font-medium cursor-pointer"
          >
            Detayları Gör <i className="ri-arrow-right-line text-sm"></i>
          </button>
        </div>
      </div>

      {/* 5. SON SATIŞLAR */}
      <div className="stat-card">
        <h3 className="text-sm font-semibold text-foreground-800 mb-4">Son Satışlar</h3>
        <div className="hidden lg:block overflow-x-auto -mx-5">
          <table className="w-full min-w-[700px]">
            <thead>
              <tr className="border-b border-background-200">
                <th className="table-header">Satış No</th><th className="table-header">Müşteri</th><th className="table-header">Kategori</th><th className="table-header">Tutar</th><th className="table-header">Kalan</th><th className="table-header">Ödeme</th><th className="table-header">Teslimat</th>
              </tr>
            </thead>
            <tbody>
              {(salesData || []).map((sale) => (
                <tr key={sale.id} className="border-b border-background-100 hover:bg-background-50 transition-colors">
                  <td className="table-cell font-medium text-primary-600">{sale.sale_no}</td><td className="table-cell">{sale.customer_name}</td><td className="table-cell text-foreground-500">{sale.product_category}</td><td className="table-cell font-medium">{formatTRY(sale.total_amount || 0)}</td><td className="table-cell">{(sale.remaining_amount || 0) > 0 ? <span className="text-red-500 font-medium">{formatTRY(sale.remaining_amount)}</span> : <span className="text-emerald-600">—</span>}</td>
                  <td className="table-cell py-2"><Badge badgeClass={paymentStatus.badgeClass(sale.payment_status)} icon={paymentStatus.icon(sale.payment_status)}>{sale.payment_status}</Badge></td>
                  <td className="table-cell py-2"><Badge badgeClass={deliveryStatus.badgeClass(sale.delivery_status)} icon={deliveryStatus.icon(sale.delivery_status)}>{sale.delivery_status}</Badge></td>
                </tr>
              ))}
              {(salesData || []).length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-sm text-foreground-400">Henüz satış kaydı yok</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="lg:hidden space-y-2.5">
          {(salesData || []).map((sale) => (
            <div key={sale.id} className="bg-background-50 border border-background-200/70 rounded-xl p-3 space-y-2">
              <div className="flex items-center justify-between"><span className="text-xs font-semibold text-primary-600">{sale.sale_no}</span><Badge badgeClass={paymentStatus.badgeClass(sale.payment_status)} icon={paymentStatus.icon(sale.payment_status)}>{sale.payment_status}</Badge></div>
              <div className="flex items-center justify-between"><div><p className="text-sm font-medium text-foreground-800">{sale.customer_name}</p><p className="text-xs text-foreground-400">{sale.product_category}</p></div><div className="text-right"><p className="text-sm font-bold text-foreground-800">{formatTRY(sale.total_amount || 0)}</p>{(sale.remaining_amount || 0) > 0 && <p className="text-xs text-red-500">Kalan: {formatTRY(sale.remaining_amount)}</p>}</div></div>
              <div className="flex items-center gap-2"><Badge badgeClass={deliveryStatus.badgeClass(sale.delivery_status)} icon={deliveryStatus.icon(sale.delivery_status)}>{sale.delivery_status}</Badge></div>
            </div>
          ))}
          {(salesData || []).length === 0 && <p className="text-sm text-foreground-400 text-center py-6">Henüz satış kaydı yok</p>}
        </div>
      </div>

      {/* 6. YAKLAŞAN TESLİMATLAR + DÖVİZ KURLARI */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="stat-card">
          <h3 className="text-sm font-semibold text-foreground-800 mb-4">Yaklaşan Teslimatlar</h3>
          <div className="space-y-2.5">
            {(upcomingDeliveries || []).map((d: any) => (
              <div key={d.id} className="flex items-center justify-between py-2 border-b border-background-100 last:border-0">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-foreground-700 truncate">{d.customer_name || '—'}</p>
                  <p className="text-xs text-foreground-400">Tarih: {d.delivery_date} · {d.status}</p>
                </div>
                <Badge badgeClass={deliveryStatus.badgeClass(d.status)} icon={deliveryStatus.icon(d.status)}>{d.status}</Badge>
              </div>
            ))}
            {(upcomingDeliveries || []).length === 0 && <p className="text-sm text-foreground-400 text-center py-4">Yaklaşan teslimat yok</p>}
          </div>
        </div>
        <div className="stat-card">
          <ExchangeRateCards />
        </div>
      </div>
    </div>
  );
}
